import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import AddCoupon from "./AddCoupon";
import { COUPON } from "~/assets/js/Helpers/coupons";
import { formatCheckoutPrice } from "~/assets/js/Format";
import RemoveIcon from "~/static/images/icons/remove.svg";
import i18n from "~/assets/js/Helpers/i18n";
import { CheckoutContext } from "~/assets/js/pages/Checkout/CheckoutProvider";
import { AddToast } from "~/assets/js/components/Toast";
import EnvCompensation from "./EnvCompensation";
import Button, { ForwardButton } from "~/assets/js/components/Button";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import Headline from "../Typography/Headline";
import { useCartContext } from "~/assets/js/global/cartContext";
import { AlertCircle, AlertTriangle } from "react-feather";
import Popup from "~/assets/js/components/Popup";
import {
    hasReusableBowls,
    getReusableBowlsPrice,
} from "~/assets/js/Helpers/reusable-bowls-utils";
import Notification from "~/assets/js/components/Notification";
import useGroupOrder from "../../global/useGroupOrder";
import { useFeatureFlagVariantKey } from "posthog-js/react";

const CheckoutSummary = (props) => {
    const { cart, actions, coupons, cartError, cartItems } = useCartContext();
    const { state, dispatch } = useContext(CheckoutContext);
    const [openEnergyFeePopup, setEnergyFeePopup] = useState(false);
    const { isParticipant } = useGroupOrder();
    const hideEnvCompensation =
        useFeatureFlagVariantKey("enviromental-widget-test") !== "control" ||
        false;

    const isReusableBowl = hasReusableBowls(cart?.items);
    const isEmailGiven = state.orderAddress?.contact?.email;

    const handleRemoveCoupon = async (code) => {
        await actions.removeCoupon(code);

        if (cartError) {
            return dispatch(
                AddToast(
                    "danger",
                    i18n.t("toasts.remove_coupon.title_error"),
                    "",
                    () => handleRemoveCoupon(code)
                )
            );
        }

        return dispatch(
            AddToast("success", i18n.t("toasts.remove_coupon.title_success"))
        );
    };

    const handleSetCompensation = async (value) => {
        await actions.updateCompensation(value);
    };

    const calculateCompensation = () => {
        switch (cart?.compensation?.level) {
            case "single":
                try {
                    return parseFloat(
                        cart?.compensation?.predicted_values?.single.value_gross
                    );
                } catch (error) {
                    return 0;
                }
            case "double":
                try {
                    return parseFloat(
                        cart?.compensation?.predicted_values?.double.value_gross
                    );
                } catch (error) {
                    return 0;
                }
            case "none":
                return 0;
        }
    };

    const calculateSubtotal = () => {
        if (!cartItems || cartItems.lenght) {
            return 0;
        }

        return cartItems.reduce(
            (subtotal, product) => subtotal + parseFloat(product.value_gross),
            0
        );
    };

    const getEnergyFeeValue = () => {
        try {
            if (cart?.energy_fee) {
                return parseFloat(cart.energy_fee.value_gross);
            }

            return 0;
        } catch (error) {
            return 0;
        }
    };

    const hasCompensation = cart?.compensation?.value_gross > 0;
    const hasEnergyFee = parseFloat(cart?.energy_fee?.value_gross || "0") > 0;

    const freeDeliveryThreshold = cart?.shipping?.free_limit
        ? cart?.shipping.free_limit
        : 0;

    const showTotalShipping =
        freeDeliveryThreshold && cart?.shipping.value_gross > 0;

    const onlyItemsPrice =
        cart?.items?.reduce((total, item) => {
            return total + parseFloat(item?.value_gross);
        }, 0) || 0;

    const amountBelowMinimumOrder =
        parseFloat(cart?.shipping?.minimal_value ?? 0) -
        parseFloat(calculateSubtotal());

    return (
        <div className="summary">
            {!isParticipant && !hideEnvCompensation && (
                <EnvCompensation setCompensation={handleSetCompensation} />
            )}

            <div style={{ position: "relative" }}>
                <Headline size={5}>{i18n.t("cart.summary.label")}</Headline>
                <div className="summary__row">
                    <div className="summary__labels-wrapper">
                        <span className="summary__label">
                            {i18n.t("cart.summary.subtotal")}
                        </span>
                        {amountBelowMinimumOrder > 0 && !isParticipant && (
                            <span className="summary__sublabel">
                                (Bis zum Mindestbestellwert fehlen noch{" "}
                                <span className="error">
                                    {formatCheckoutPrice(
                                        amountBelowMinimumOrder.toString(),
                                        0,
                                        "€",
                                        0
                                    )}
                                </span>
                                )
                            </span>
                        )}
                    </div>
                    <div className="summary__labels-wrapper">
                        <span className="summary__sublabel">
                            {formatCheckoutPrice(
                                calculateSubtotal(),
                                0,
                                "€",
                                0
                            )}
                        </span>
                    </div>
                </div>

                {!!showTotalShipping && !isParticipant && (
                    <div className="summary__row">
                        <div className="summary__labels-wrapper">
                            <span className="summary__label">
                                {i18n.t("cart.summary.shipment")}
                            </span>
                            <span className="summary__sublabel">
                                {i18n.t("cart.summary.freeCharge", {
                                    threshold: formatCheckoutPrice(
                                        freeDeliveryThreshold,
                                        0,
                                        "€"
                                    ),
                                })}
                            </span>
                        </div>

                        <span className="summary__sublabel">
                            {formatCheckoutPrice(
                                cart?.shipping.value_gross || 0,
                                0,
                                "€"
                            )}
                        </span>
                    </div>
                )}

                {!isParticipant && (
                    <div className="summary__row">
                        <span className="summary__label">
                            {" "}
                            {isEmailGiven
                                ? i18n.t("cart.summary.voucher")
                                : "Gutscheine kannst du später eingeben"}
                        </span>
                        <AddCoupon disabled={!isEmailGiven} />
                    </div>
                )}

                {coupons &&
                    coupons.map((coupon) => {
                        if (coupon.valid === COUPON.VALID) {
                            return (
                                <div key={coupon.code} className="summary__row">
                                    <span className="summary__sublabel summary__coupon">
                                        Code: {coupon.code.toUpperCase()}
                                        <img
                                            className="summary__remove-coupon"
                                            src={RemoveIcon}
                                            alt="remove-coupon"
                                            onClick={() =>
                                                handleRemoveCoupon(coupon.code)
                                            }
                                        />
                                    </span>
                                    <span className="summary__sublabel">
                                        {" "}
                                        {formatCheckoutPrice(
                                            cart?.discount?.value_gross_coupon,
                                            0,
                                            "€"
                                        )}
                                    </span>
                                </div>
                            );
                        }
                    })}

                {hasCompensation && !isParticipant && (
                    <div className="summary__row">
                        <span className="summary__label">
                            {i18n.t("cart.compensation")}
                        </span>
                        <span className="summary__sublabel">
                            {formatCheckoutPrice(
                                calculateCompensation(),
                                0,
                                "€",
                                0
                            )}
                        </span>
                    </div>
                )}

                {isReusableBowl && !isParticipant && (
                    <div className="summary__row">
                        <span className="summary__label">
                            {i18n.t("cart.reusableBowlsPrice")}
                        </span>
                        <span className="summary__sublabel">
                            {formatCheckoutPrice(
                                getReusableBowlsPrice(cart?.items),
                                0,
                                "€",
                                0
                            )}
                        </span>
                    </div>
                )}

                {hasEnergyFee && !isParticipant && (
                    <>
                        <div className="summary__row">
                            <span className="summary__label">
                                {i18n.t("cart.energyFee.title")}
                                <AlertCircle
                                    onClick={() => setEnergyFeePopup(true)}
                                />
                            </span>
                            <span className="summary__sublabel">
                                {formatCheckoutPrice(
                                    getEnergyFeeValue(),
                                    0,
                                    "€",
                                    0
                                )}
                            </span>
                        </div>
                        <Popup open={openEnergyFeePopup}>
                            <AlertCircle
                                size={32}
                                scale={2}
                                className="env-compensation__info-icon"
                            />
                            <Headline
                                size={5}
                                className="env-compensation__info-title">
                                {i18n.t("cart.energyFee.title")}
                            </Headline>
                            <p className="env-compensation__info-text">
                                {i18n.t("cart.energyFee.text")}
                            </p>
                            <Button
                                variant="secondary"
                                size="SM"
                                onClick={() => setEnergyFeePopup(false)}
                                label={i18n.t("cart.energyFee.close")}
                            />
                        </Popup>
                    </>
                )}
                {/* <GreenClubTagDiscount discount={membershipDiscount} />  Will be added in the future*/}
                <hr className="summary__divider" />
                <div className="summary__row">
                    <div className="summary__labels-wrapper">
                        <span className="summary__total">
                            {i18n.t("cart.summary.total")}
                        </span>
                        <span className="summary__sublabel">
                            {i18n.t("cart.summary.vat")}
                        </span>
                    </div>
                    <span className="summary__total">
                        {isParticipant
                            ? formatCheckoutPrice(
                                  onlyItemsPrice >= 0 ? onlyItemsPrice : 0,
                                  0,
                                  "€"
                              )
                            : formatCheckoutPrice(
                                  cart?.value_gross >= 0
                                      ? cart?.value_gross
                                      : 0,
                                  0,
                                  "€"
                              )}
                    </span>
                </div>

                {isParticipant && (
                    <Notification
                        customClass="summary__groupOrder-notification"
                        type="warning"
                        icon={<AlertTriangle />}>
                        <span className="summary__notification-desc">
                            {i18n.t("memberGroupOrder.checkoutBanner")}
                        </span>
                    </Notification>
                )}

                <DesktopForwardButton
                    {...props}
                    isParticipant={isParticipant}
                />
            </div>
        </div>
    );
};

const DesktopForwardButton = ({
    handleDesktopMoveForward,
    desktopForwardLabel,
    loadingForwardButton = false,
    forwardButtonDisabled = false,
    termsComponent,
    isParticipant,
}) => {
    const { isDesktop } = useViewport();
    if (isDesktop) {
        return (
            <>
                {termsComponent && (
                    <div style={{ marginTop: "24px" }}>{termsComponent}</div>
                )}
                {isParticipant ? (
                    <Button
                        loading={loadingForwardButton}
                        label={i18n.t("memberGroupOrder.commitOrderButton")}
                        onClick={handleDesktopMoveForward}
                        disabled={forwardButtonDisabled}
                    />
                ) : (
                    <ForwardButton
                        loading={loadingForwardButton}
                        label={desktopForwardLabel}
                        onClick={handleDesktopMoveForward}
                        disabled={forwardButtonDisabled}
                    />
                )}
            </>
        );
    }
    return null;
};

const summaryProps = {
    handleDesktopMoveForward: PropTypes.func,
    desktopForwardLabel: PropTypes.string,
    loadingForwardButton: PropTypes.bool,
    forwardButtonDisabled: PropTypes.bool,
    termsComponent: PropTypes.node,
};

CheckoutSummary.propTypes = summaryProps;
DesktopForwardButton.propTypes = {
    ...summaryProps,
    isParticipant: PropTypes.bool,
};

CheckoutSummary.defaultProps = {
    handleDesktopMoveForward: () => {},
    desktopForwardLabel: i18n.t("cart.continue"),
    loadingForwardButton: false,
    forwardButtonDisabled: false,
};

export default CheckoutSummary;
